import { Stack, Spacer } from 'common/ui/Layout';

import { Button } from 'common/ui/Button';
import Text from 'common/ui/Text';
import { Colors } from 'styles';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { rootTheme } from 'theme/rootTheme';

const DraggableDivForDialog = styled(motion.div)`
  background: ${Colors.gray3};
  border-radius: ${rootTheme.radius.sm}px;
  width: 37rem;
  position: absolute;
  left: 50%;
  top: 50%;
  margin-left: -11rem;
  margin-top: -5.25rem;
  z-index: 2; /* The ViewType dropdowns have z-index equal to 1 */
  padding: 1rem;
`;

// Though this syntax may seem weird (why not just put the css in the exported component?), it helps tests interpret the framer-motion package
const GenericDraggableDiv = styled(motion.div)`
  z-index: 9;
  position: absolute;
  padding-left: 1rem;
`;

type BoundingBox2D = {
  top?: number;
  left?: number;
  bottom?: number;
  right?: number;
};

export const DraggableContainer = ({
  dragConstraintRef,
  children,

  ...props
}: {
  dragConstraintRef: {
    current: null | HTMLDivElement;
  };
  children: React.ReactNode;
}): React.ReactElement => (
  <GenericDraggableDiv {...props} drag dragConstraints={dragConstraintRef} dragMomentum={false}>
    <DragIndicatorIcon
      css={`
        position: absolute;
        left: 15px;
        top: 11px;
        cursor: move;
      `}
    />
    {children}
  </GenericDraggableDiv>
);

export const DraggableDialog = ({
  children,
  onAccept,
  onReject,
  componentName,
  dragConstraintRef,
  rejectVariant = 'ghost',
  acceptVariant = 'primary',
  ...props
}: {
  children: React.ReactNode;
  onAccept?: () => Promise<undefined> | undefined;
  onReject?: () => void;
  dragConstraintRef: Partial<BoundingBox2D> | React.RefObject<Element>;
  componentName: string;
  rejectVariant?: 'primary' | 'secondary' | 'light' | 'error' | 'ghost';
  acceptVariant?: 'primary' | 'secondary' | 'light' | 'error' | 'ghost';
}): React.ReactElement => {
  return (
    <DraggableDivForDialog {...props} drag dragMomentum={false} dragConstraints={dragConstraintRef}>
      <DragIndicatorIcon
        css={`
          position: absolute;
          color: ${Colors.gray7};
          top: 5px;
          left: 2px;
          font-size: 3rem;
          cursor: move;
        `}
        data-testid="draggable-dialog"
      />
      <Stack
        vertical
        space="medium"
        alignX="center"
        alignY="start"
        stretchY={true}
        padding="medium"
      >
        <Text variant="display1" align="center" color="white" display="block">
          {children}
        </Text>
        <Stack padding="xsmall" alignX="center" alignY="center" css="width:90%;">
          <Button
            variant={rejectVariant}
            onClick={onReject}
            data-testid="reject-btn"
            id={`${componentName}-dialog-reject`}
            useDefaultPadding
          >
            Reject
          </Button>
          <Spacer />
          <Button
            variant={acceptVariant}
            onClick={onAccept}
            data-testid="accept-btn"
            data-analytics-name="dialog-accept"
            id={`${componentName}-dialog-accept`}
          >
            Accept
          </Button>
        </Stack>
      </Stack>
    </DraggableDivForDialog>
  );
};
